<template>
  <div v-if="shouldShowPagination" class="data-grid-pagination" data-testid="pagination-full">
    <div class="pagination-full">
      <!-- Previous button -->
      <Button
        v-if="canGoPrevious"
        type="ghost"
        variant="gray"
        icon="general.chevronLeft"
        data-testid="prev-button"
        @click="goToPage(currentPage - 1)"
      />

      <!-- Page numbers -->
      <template v-for="page in displayPages" :key="page">
        <a
          v-if="typeof page === 'number'"
          href="#"
          :class="[
            'pagination-link',
            page === currentPage ? 'pagination-link--active' : 'pagination-link--inactive'
          ]"
          :data-testid="`page-${page}`"
          @click.prevent="goToPage(page)"
        >
          {{ page }}
        </a>
        <span v-else class="pagination-ellipsis" data-testid="ellipsis">{{ page }}</span>
      </template>

      <!-- Next button -->
      <Button
        v-if="canGoNext"
        type="ghost"
        variant="gray"
        icon="general.chevronRight"
        data-testid="next-button"
        @click="goToPage(currentPage + 1)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { Button } from '@/storybook';
import { useFullPagination, type PaginationProps } from '../../composables/usePagination';

interface Props extends PaginationProps {
  totalItems: number;
  perPage: number;
}

interface Emits {
  (e: 'page-change', page: number): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const { canGoPrevious, canGoNext, shouldShowPagination, displayPages } = useFullPagination(props);

const goToPage = (page: number) => {
  if (page >= 1 && page <= props.totalPages && page !== props.currentPage) {
    emit('page-change', page);
  }
};
</script>

<style scoped>
.data-grid-pagination {
  @apply flex items-center justify-center py-4;
}

.pagination-full {
  @apply flex items-center gap-2;
}

.pagination-link {
  @apply inline-flex items-center justify-center w-8 h-8 text-base rounded transition-colors duration-200;
  @apply hover:no-underline focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2;
}

.pagination-link--active {
  @apply text-blue-600 dark:text-blue-400 font-bold underline hover:text-blue-700 dark:hover:text-blue-300;
}

.pagination-link--inactive {
  @apply text-zinc-500 dark:text-zinc-400 font-normal hover:bg-zinc-100 dark:hover:bg-zinc-700 hover:text-zinc-700 dark:hover:text-zinc-300;
}

.pagination-ellipsis {
  @apply inline-flex items-center justify-center w-8 h-8 text-sm text-zinc-400 dark:text-zinc-500;
}
</style>